import { AlertTriangle, Bell, CheckCircle2, Loader2, Printer, Usb } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_CONFIG } from '../../config/api';
import { PageHeader } from '../common/PageHeader';
import { ui } from '../../styles/ui';
import {
  DEFAULT_PRINTER_CONFIG,
  getAuthorizedPrinter,
  getPrinterConfig,
  isWebUSBSupported,
  printTestLabelViaUSB,
  requestPrinter,
  savePrinterConfig,
} from '../../utils/tscPrinter';

const PRINTER_FIELDS = [
  { key: 'widthMm', label: 'Label width (mm)', min: 10, max: 200, step: 1 },
  { key: 'heightMm', label: 'Label height (mm)', min: 10, max: 300, step: 1 },
  { key: 'gapMm', label: 'Gap between labels (mm)', min: 0, max: 20, step: 0.5 },
  { key: 'darkness', label: 'Darkness (0-15)', min: 0, max: 15, step: 1 },
  { key: 'speed', label: 'Print speed (ips)', min: 1, max: 6, step: 1 },
  { key: 'copies', label: 'Copies per print', min: 1, max: 10, step: 1 },
];

const PrinterConfigScreen = () => {
  const [config, setConfig] = useState(DEFAULT_PRINTER_CONFIG);
  const [deviceName, setDeviceName] = useState(null);
  const [connecting, setConnecting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [status, setStatus] = useState(null); // { type: 'success' | 'error', message }
  const supported = isWebUSBSupported();

  useEffect(() => {
    setConfig(getPrinterConfig());
    if (supported) {
      getAuthorizedPrinter().then((device) => {
        if (device) setDeviceName(device.productName || `USB device (${device.vendorId}:${device.productId})`);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateField = (key, value) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    savePrinterConfig(next);
  };

  const handleConnect = async () => {
    setStatus(null);
    setConnecting(true);
    try {
      const device = await requestPrinter();
      setDeviceName(device.productName || `USB device (${device.vendorId}:${device.productId})`);
      setStatus({ type: 'success', message: 'Printer paired. It will reconnect automatically next time.' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to pair printer.' });
    } finally {
      setConnecting(false);
    }
  };

  const handleTestPrint = async () => {
    setStatus(null);
    setTesting(true);
    try {
      await printTestLabelViaUSB(config);
      setStatus({ type: 'success', message: 'Test label sent to printer.' });
    } catch (err) {
      setStatus({ type: 'error', message: err.message || 'Failed to print test label.' });
    } finally {
      setTesting(false);
    }
  };

  return (
    <div className={`${ui.card} p-5`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 rounded-lg bg-indigo-600 shrink-0">
          <Printer className="h-5 w-5 text-white" />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Label Printer (TSC)</h3>
          <p className="text-xs text-gray-500 dark:text-gray-400">Connect a USB TSC printer and configure label size for barcode printing</p>
        </div>
      </div>

      {!supported && (
        <div className="flex items-start gap-2.5 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/50 mb-4">
          <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-800 dark:text-amber-300">
            WebUSB is not supported in this browser. Open the portal in Chrome or Edge to connect a USB label printer.
          </p>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-3 bg-gray-50 dark:bg-gray-900/50 rounded-lg mb-4">
        <div className="flex items-center gap-2.5">
          <Usb className={`h-4 w-4 shrink-0 ${deviceName ? 'text-green-600 dark:text-green-400' : 'text-gray-400'}`} />
          <div>
            <div className="text-sm font-medium text-gray-800 dark:text-gray-100">
              {deviceName || 'No printer paired'}
            </div>
            {deviceName && <div className="text-xs text-gray-500 dark:text-gray-400">Reconnects automatically on this device</div>}
          </div>
        </div>
        <button
          type="button"
          onClick={handleConnect}
          disabled={!supported || connecting}
          className="px-3.5 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
        >
          {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Usb className="h-4 w-4" />}
          {deviceName ? 'Change printer' : 'Connect printer'}
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
        {PRINTER_FIELDS.map((field) => (
          <label key={field.key} className="block">
            <span className="text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide">{field.label}</span>
            <input
              type="number"
              min={field.min}
              max={field.max}
              step={field.step}
              value={config[field.key]}
              onChange={(e) => updateField(field.key, Number(e.target.value))}
              className={`mt-1 ${ui.input}`}
            />
          </label>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
        <button
          type="button"
          onClick={handleTestPrint}
          disabled={!supported || testing}
          className="px-3.5 py-2 bg-gray-800 hover:bg-gray-900 dark:bg-gray-700 dark:hover:bg-gray-600 disabled:opacity-50 text-white rounded-lg font-medium text-sm transition-colors flex items-center justify-center gap-2"
        >
          {testing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
          Print test label
        </button>
        {status && (
          <div className={`flex items-center gap-2 text-sm ${status.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
            {status.type === 'success' ? <CheckCircle2 className="h-4 w-4 shrink-0" /> : <AlertTriangle className="h-4 w-4 shrink-0" />}
            {status.message}
          </div>
        )}
      </div>
    </div>
  );
};

export const SettingsScreen = () => (
  <div className="space-y-4">
    <PageHeader title="System Settings" subtitle="Configure system preferences and administrative controls" />

    <PrinterConfigScreen />

    <div className={`${ui.card} p-5`}>
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-blue-600 shrink-0">
          <Bell className="h-5 w-5 text-white" />
        </div>
        <div>
          <span className="text-sm font-semibold text-gray-900 dark:text-white">API Configuration</span>
          <p className="text-xs text-gray-500 dark:text-gray-400">Manage API endpoints and settings</p>
        </div>
      </div>
      <div className="mt-3 text-sm text-green-600 dark:text-green-400 font-medium break-all">
        Base URL: {API_CONFIG.baseURL}
      </div>
    </div>
  </div>
);
